import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { useListWorkspaces } from '@sunstone/api-client/hooks';
import {
  Button,
  Checkbox,
  Input,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sunstone/ui';
import { Search, Server } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { EmptyState } from '@/components/ui/EmptyState';
import { PromoteWorkspacesDialog } from '@/components/ui/PromoteWorkspacesDialog';
import { ReleaseCombobox } from '@/components/ui/ReleaseCombobox';
import { StatusBadge, StatusDot } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { type Blip, BlipsStrip } from '@/features/health/HealthBlips';
import { getBulkPromoteEligibility } from '@/features/workspaces/bulkPromoteEligibility';
import {
  ENV_LABELS,
  STATUS_FILTER_OPTIONS,
  workspaceEnvironmentKey,
  workspaceStatusFromKey,
  workspaceStatusKey,
} from '@/features/workspaces/utils';
import { WorkspaceVersionStatus } from '@/features/workspaces/WorkspaceVersionStatus';
import { useDebounce } from '@/hooks/useDebounce';

const PAGE_SIZE = 60;

const ALL = '__all__';

// ─── Workspace card ───────────────────────────────────────────────────────────

function WorkspaceCard({
  workspace,
  tenantId,
  healthBlipsByEntityId,
  healthLoading,
  canSelect,
  selected,
  onToggleSelected,
  selectionDisabledReason,
}: {
  workspace: Workspace;
  tenantId?: string;
  healthBlipsByEntityId?: Record<string, Blip[]>;
  healthLoading?: boolean;
  canSelect?: boolean;
  selected?: boolean;
  onToggleSelected?: () => void;
  selectionDisabledReason?: string;
}) {
  const navigate = useNavigate();
  const envKey = workspaceEnvironmentKey(workspace.environment);
  const statusKey = workspaceStatusKey(workspace.status);
  const envLabel = ENV_LABELS[envKey] ?? envKey;
  const isProduction = envKey === 'WORKSPACE_ENVIRONMENT_PRODUCTION';
  const lastUpdated = workspace.updatedAt
    ? timestampDate(workspace.updatedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

  const handleClick = () => {
    if (!tenantId) return;
    navigate(
      `/tenants/${tenantId}/deployments/${workspace.deploymentId}/workspaces/${workspace.id}`,
    );
  };

  const checkbox = canSelect && onToggleSelected && (
    <span className="shrink-0">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Checkbox
                checked={!!selected}
                disabled={!!selectionDisabledReason}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={() => onToggleSelected()}
                aria-label={`Select ${workspace.displayName}`}
              />
            </span>
          </TooltipTrigger>
          {selectionDisabledReason && <TooltipContent>{selectionDisabledReason}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    </span>
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`rounded-lg border bg-card flex flex-col gap-0 overflow-hidden cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group w-full text-left ${
        isProduction ? 'border-border ring-1 ring-border/50' : 'border-border'
      }`}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {checkbox}
            <StatusDot status={statusKey} size="sm" />
            <span
              className={`text-xs font-mono ${isProduction ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-muted-foreground'}`}
            >
              {envLabel}
            </span>
          </div>
          <p className="font-medium text-foreground text-sm font-mono truncate">
            {workspace.displayName}
          </p>
          {workspace.slug && (
            <p className="text-xs font-mono text-muted-foreground truncate">{workspace.slug}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={statusKey} className="text-xs shrink-0" />
        </div>
      </div>

      {/* Overall Health */}
      {(healthBlipsByEntityId || healthLoading) && (
        <div className="px-4 pb-3">
          <BlipsStrip
            blips={healthBlipsByEntityId?.[workspace.id] ?? []}
            isLoading={healthLoading}
          />
        </div>
      )}

      {/* Meta strip */}
      <div className="px-4 py-3 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-3 text-xs h-12">
        <WorkspaceVersionStatus workspace={workspace} className="font-mono text-muted-foreground" />
        <span className="text-muted-foreground/60 text-right">{lastUpdated}</span>
      </div>
    </button>
  );
}

// ─── WorkspaceList ────────────────────────────────────────────────────────────

interface WorkspaceListProps {
  deploymentId: string;
  tenantId: string;
  healthBlipsByEntityId?: Record<string, Blip[]>;
  healthLoading?: boolean;
}

export function WorkspaceList({
  deploymentId,
  tenantId,
  healthBlipsByEntityId,
  healthLoading,
}: WorkspaceListProps) {
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [releaseIdFilter, setReleaseIdFilter] = useState<string | null>(null);
  const canBulkPromote = hasPermission('promote:workspace') || hasPermission('upgrade:workspace');
  const [selected, setSelected] = useState<Map<string, Workspace>>(new Map());
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);

  const toggleSelected = useCallback((w: Workspace) => {
    setSelected((prev) => {
      const updated = new Map(prev);
      if (updated.has(w.id)) {
        updated.delete(w.id);
      } else {
        updated.set(w.id, w);
      }
      return updated;
    });
  }, []);

  const debouncedSearch = useDebounce(search);

  const {
    data: workspacesData,
    isLoading,
    error,
  } = useListWorkspaces(deploymentId, token, {
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
    statusFilter: workspaceStatusFromKey(statusFilter),
    releaseIdFilter: releaseIdFilter ?? undefined,
  });

  const totalCount = workspacesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setPage(1);
  }, []);
  const handleStatusFilterChange = useCallback((v: string | null) => {
    setStatusFilter(v);
    setPage(1);
  }, []);
  const handleReleaseFilterChange = useCallback((v: string | null) => {
    setReleaseIdFilter(v);
    setPage(1);
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Failed to load workspaces.
      </div>
    );
  }

  const workspaces = workspacesData?.workspaces ?? [];

  const handleNewWorkspace = () => {
    navigate(`/tenants/${tenantId}/deployments/${deploymentId}/workspaces/new`);
  };

  if (!isLoading && workspaces.length === 0 && !search && !statusFilter && !releaseIdFilter) {
    const canCreateWorkspace = hasPermission('create:workspace');
    return (
      <EmptyState
        icon={<Server className="w-8 h-8 text-muted-foreground/50" />}
        title="No workspaces yet"
        description="Add a workspace to start provisioning Kubernetes clusters in this deployment."
        actionLabel={canCreateWorkspace ? 'New Workspace' : undefined}
        onAction={canCreateWorkspace ? handleNewWorkspace : undefined}
      />
    );
  }

  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('ellipsis');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 w-56"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selected.size} selected</span>
              <Button variant="outline" size="sm" onClick={() => setSelected(new Map())}>
                Clear
              </Button>
              <Button size="sm" onClick={() => setPromoteDialogOpen(true)}>
                Promote / Upgrade
              </Button>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium">Status:</span>
            <Select
              value={statusFilter ?? ALL}
              onValueChange={(v) => handleStatusFilterChange(v === ALL ? null : v)}
            >
              <SelectTrigger className="h-9 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any status</SelectItem>
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium">Release:</span>
            <ReleaseCombobox
              token={token}
              value={releaseIdFilter ?? ''}
              onChange={(v) => handleReleaseFilterChange(v || null)}
              deployableOnly={false}
              allowClear
              placeholder="Any release"
              className="h-9 w-40 text-xs"
            />
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center p-8 text-muted-foreground">Loading...</div>
      ) : workspaces.length === 0 ? (
        <div className="flex items-center justify-center p-8 text-muted-foreground">
          No workspaces match your search.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {workspaces.map((w) => {
            const eligibility = getBulkPromoteEligibility(
              w,
              [...selected.values()].filter((s) => s.id !== w.id),
            );
            return (
              <WorkspaceCard
                key={w.id}
                workspace={w}
                tenantId={tenantId}
                healthBlipsByEntityId={healthBlipsByEntityId}
                healthLoading={healthLoading}
                canSelect={canBulkPromote}
                selected={selected.has(w.id)}
                onToggleSelected={() => toggleSelected(w)}
                selectionDisabledReason={eligibility.eligible ? undefined : eligibility.reason}
              />
            );
          })}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <span className="text-sm text-muted-foreground tabular-nums px-2">
                  Page {page} of {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious onClick={() => setPage(page - 1)} disabled={page <= 1} />
              </PaginationItem>
              {pages.map((p, i) => {
                const key = p === 'ellipsis' ? `ellipsis-${i}` : p;
                return p === 'ellipsis' ? (
                  <PaginationItem key={key}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={key}>
                    <PaginationLink isActive={p === page} onClick={() => setPage(p)}>
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext onClick={() => setPage(page + 1)} disabled={page >= totalPages} />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      <PromoteWorkspacesDialog
        open={promoteDialogOpen}
        onOpenChange={setPromoteDialogOpen}
        workspaces={[...selected.values()]}
        onSuccess={() => setSelected(new Map())}
      />
    </div>
  );
}
